import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, ChevronDown, ChevronUp, AlertTriangle, Send, Loader2, FileText, RefreshCcw, CheckCircle2, Target, BarChart3, FileBadge, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import api, { auth } from "../lib/api";
import { ActivityLog } from "../components/ActivityLog";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { languages as allLanguages } from "../lib/languages";

const getScoreColor = (score) => {
  if (!score) return "bg-slate-100 text-slate-500 border-slate-200";
  if (score <= 2) return "bg-red-50 text-red-700 border-red-200";
  if (score === 3) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
};

const getConfidenceColor = (confidence) => {
  if (confidence === "low") return "confidence-low";
  if (confidence === "medium") return "confidence-medium";
  return "confidence-high";
};

function DomainRow({ score, onUpdate, isLocked }) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const effectiveScore = score.human_score !== null ? score.human_score : (score.ai_score || 0);

  const [localScore, setLocalScore] = useState(effectiveScore?.toString() || "");
  const [localNote, setLocalNote] = useState(score.human_note || "");
  const [localGoal, setLocalGoal] = useState(score.participant_goal || false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const val = score.human_score !== null ? score.human_score : (score.ai_score || 0);
    setLocalScore(val?.toString());
    setLocalNote(score.human_note || "");
    setLocalGoal(score.participant_goal || false);
  }, [score]);

  const hasOverride = score.human_override || (score.human_score !== null && score.human_score !== score.ai_score);
  const needsReview = score.needs_human_review && !score.human_override && effectiveScore === 0;

  const rowBgClass = needsReview ? "bg-amber-50" : "hover:bg-slate-50/50";

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(score.domain_key, {
        human_score: localScore ? parseInt(localScore) : 0,
        human_note: localNote,
        participant_goal: localGoal,
        human_override: true
      });
      const translatedDomain = t(`domain_${score.domain_key}`) !== `domain_${score.domain_key}` ? t(`domain_${score.domain_key}`) : score.domain_label;
      toast.success(t('domain_updated_toast', { domain: translatedDomain }) || `${translatedDomain} updated`);
      setIsOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('error_save') || "Failed to save";
      toast.error(errorMsg);
      console.error("Save error:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={`border-b border-slate-100 last:border-b-0 ${rowBgClass}`}>
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center gap-4 p-4 cursor-pointer transition-colors"
            data-testid={`domain-row-${score.domain_key}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">
                  {t(`domain_${score.domain_key}`) !== `domain_${score.domain_key}`
                    ? t(`domain_${score.domain_key}`)
                    : score.domain_label}
                </span>
                {needsReview && (
                  <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                    {t('needs_review')}
                  </Badge>
                )}
                {hasOverride && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    {t('reviewed')}
                  </Badge>
                )}
                {localGoal && (
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    {t('goal_label')}
                  </Badge>
                )}
              </div>
            </div>

            <div className="text-center w-20">
              <span className="text-xs text-slate-400 block mb-1">{t('ai_score_label')}</span>
              <span className={`score-badge ${getScoreColor(score.ai_score)}`}>
                {score.ai_score || 0}
              </span>
            </div>

            <div className="text-center w-24">
              <span className="text-xs text-slate-400 block mb-1">{t('confidence_label')}</span>
              <Badge
                variant="outline"
                className={`text-xs ${getConfidenceColor(score.ai_confidence)}`}
              >
                {score.ai_confidence || "N/A"}
              </Badge>
            </div>

            <div className="text-center w-20">
              <span className="text-xs text-slate-400 block mb-1">{t('final_score_label')}</span>
              <span className={`score-badge ${getScoreColor(effectiveScore)}`}>
                {effectiveScore}
              </span>
            </div>

            <div className="w-8">
              {isOpen ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 bg-slate-50/50 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {t('ai_justification_title')}
                </h4>
                <p className="text-sm text-slate-700 bg-white p-3 rounded-sm border border-slate-200 h-full">
                  {score.ai_justification || t('no_justification')}
                </p>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {t('evidence_quotes_title')}
                </h4>
                {score.ai_evidence_quotes?.length > 0 ? (
                  <ul className="space-y-2">
                    {score.ai_evidence_quotes.map((quote, idx) => (
                      <li key={idx} className="text-sm text-slate-600 bg-white p-2 rounded-sm border border-slate-200 italic">
                        "{quote}"
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500 italic">{t('no_evidence')}</p>
                )}
              </div>
            </div>

            <Collapsible>
              <CollapsibleTrigger className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <ChevronDown className="w-3 h-3" /> {t('show_score_definitions')}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="grid gap-2 text-sm max-h-40 overflow-y-auto">
                  {Object.entries(score.level_descriptions || {}).map(([level, desc]) => (
                    <div
                      key={level}
                      className={`flex gap-3 p-2 rounded-sm ${parseInt(level) === effectiveScore ? "bg-slate-200" : "bg-white"}`}
                    >
                      <span className={`score-badge text-xs ${getScoreColor(parseInt(level))}`}>{level}</span>
                      <span className="text-slate-600">{desc}</span>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {!isLocked ? (
              <div className="bg-white p-4 rounded-sm border border-slate-200 space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t('review_adjust_title')} ({t(`domain_${score.domain_key}`) !== `domain_${score.domain_key}` ? t(`domain_${score.domain_key}`) : score.domain_label})
                  </h4>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm text-slate-600 block mb-1.5">{t('final_score_range_label')}</label>
                    <Select
                      value={localScore}
                      onValueChange={(val) => setLocalScore(val)}
                    >
                      <SelectTrigger className="h-10 rounded-sm border-slate-300">
                        <SelectValue placeholder={t('select_score_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{t('score_0_desc')}</SelectItem>
                        <SelectItem value="1">{t('score_1_desc')}</SelectItem>
                        <SelectItem value="2">{t('score_2_desc')}</SelectItem>
                        <SelectItem value="3">{t('score_3_desc')}</SelectItem>
                        <SelectItem value="4">{t('score_4_desc')}</SelectItem>
                        <SelectItem value="5">{t('score_5_desc')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`goal-${score.domain_key}`}
                        checked={localGoal}
                        onCheckedChange={setLocalGoal}
                      />
                      <label
                        htmlFor={`goal-${score.domain_key}`}
                        className="text-sm text-slate-600 cursor-pointer"
                      >
                        {t('mark_as_goal')}
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-slate-600 block mb-1.5">{t('notes_label')}</label>
                  <Textarea
                    value={localNote}
                    onChange={(e) => setLocalNote(e.target.value)}
                    placeholder={t('add_notes_placeholder')}
                    className="rounded-sm border-slate-300 resize-none"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    size="sm"
                    className="bg-slate-700 hover:bg-slate-800 rounded-sm"
                  >
                    {saving ? t('saving') : t('confirm_save')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 p-3 italic text-sm text-slate-500 text-center">
                {t('assessment_submitted_locked')}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function ReportView({ caseId }) {
  const { t } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Edit state
  const [editSummary, setEditSummary] = useState("");
  const [editSubjective, setEditSubjective] = useState("");
  const [editObjective, setEditObjective] = useState("");
  const [editAssessment, setEditAssessment] = useState("");
  const [editPlan, setEditPlan] = useState("");
  const [editStrengths, setEditStrengths] = useState([]);
  const [editActionPlan, setEditActionPlan] = useState([]);
  const [editContradictions, setEditContradictions] = useState([]);

  useEffect(() => {
    loadReport();
  }, [caseId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await api.getReport(caseId);
      setReport(res.data);
      if (res.data) {
        setEditSummary(res.data.executive_summary || "");
        setEditSubjective(res.data.subjective || "");
        setEditObjective(res.data.objective || "");
        setEditAssessment(res.data.assessment || "");
        setEditPlan(res.data.plan || "");
        setEditStrengths([...(res.data.key_strengths || [])]);
        setEditActionPlan([...(res.data.action_plan || [])]);
        setEditContradictions([...(res.data.apparent_contradictions || [])]);
      }
    } catch (error) {
      console.error("Failed to load report", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await api.generateReport(caseId);
      setReport(res.data);
      setEditSummary(res.data.executive_summary || "");
      setEditSubjective(res.data.subjective || "");
      setEditObjective(res.data.objective || "");
      setEditAssessment(res.data.assessment || "");
      setEditPlan(res.data.plan || "");
      setEditStrengths([...(res.data.key_strengths || [])]);
      setEditActionPlan([...(res.data.action_plan || [])]);
      setEditContradictions([...(res.data.apparent_contradictions || [])]);
      toast.success(t('report_generated_success') || "Report generated successfully");
    } catch (error) {
      console.error("Failed to generate report", error);
      toast.error(t('error_generate_report') || "Failed to generate report. Ensure transcript and scores exist.");
    } finally {
      setGenerating(false);
    }
  };

  const handleRevert = async () => {
    if (!window.confirm(t('revert_report_confirm') || "Are you sure you want to revert to the original AI-generated report? All manual edits will be lost.")) return;

    try {
      setReverting(true);
      const res = await api.revertReport(caseId);
      setReport(res.data);
      setEditSummary(res.data.executive_summary || "");
      setEditSubjective(res.data.subjective || "");
      setEditObjective(res.data.objective || "");
      setEditAssessment(res.data.assessment || "");
      setEditPlan(res.data.plan || "");
      setEditStrengths([...(res.data.key_strengths || [])]);
      setEditActionPlan([...(res.data.action_plan || [])]);
      setEditContradictions([...(res.data.apparent_contradictions || [])]);
      setIsEditing(false);
      toast.success(t('report_revert_success') || "Reverted to original AI report");
    } catch (error) {
      console.error("Failed to revert report", error);
      toast.error(t('error_revert_report') || "Failed to revert report");
    } finally {
      setReverting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        executive_summary: editSummary,
        subjective: editSubjective,
        objective: editObjective,
        assessment: editAssessment,
        plan: editPlan,
        key_strengths: editStrengths,
        action_plan: editActionPlan,
        apparent_contradictions: editContradictions
      };

      const res = await api.updateReport(caseId, updateData);
      setReport(res.data);
      setIsEditing(false);
      toast.success(t('report_updated_success') || "Report updated successfully");
    } catch (error) {
      console.error("Failed to save report", error);
      toast.error(t('error_save_report') || "Failed to save report changes");
    } finally {
      setSaving(false);
    }
  };

  const handleStrengthChange = (index, value) => {
    const newStrengths = [...editStrengths];
    newStrengths[index] = value;
    setEditStrengths(newStrengths);
  };

  const handleActionPlanChange = (index, field, value) => {
    const newPlan = [...editActionPlan];
    newPlan[index] = { ...newPlan[index], [field]: value };
    setEditActionPlan(newPlan);
  };

  const handleContradictionChange = (index, value) => {
    const newContradictions = [...editContradictions];
    newContradictions[index] = value;
    setEditContradictions(newContradictions);
  };

  const addStrength = () => setEditStrengths([...editStrengths, ""]);
  const removeStrength = (index) => setEditStrengths(editStrengths.filter((_, i) => i !== index));

  const addActionGoal = () => setEditActionPlan([...editActionPlan, { domain: "", goal: "" }]);
  const removeActionGoal = (index) => setEditActionPlan(editActionPlan.filter((_, i) => i !== index));

  const addContradiction = () => setEditContradictions([...editContradictions, ""]);
  const removeContradiction = (index) => setEditContradictions(editContradictions.filter((_, i) => i !== index));

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} ${t('copy_success') || 'copied to clipboard'}`);
  };

  const copySection = (sectionData, label) => {
    if (Array.isArray(sectionData)) {
      // Format array data (strengths/action plan)
      if (label === t('action_plan_title') || label === "Action Plan Targets") {
        const text = sectionData.map(i => `${i.domain}: ${i.goal}`).join("\n");
        copyToClipboard(text, label);
      } else {
        const text = sectionData.join("\n");
        copyToClipboard(text, label);
      }
    } else {
      copyToClipboard(sectionData, label);
    }
  };


  if (loading) return <div className="p-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />{t('loading_report')}</div>;

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-md border border-slate-200 text-center animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4">
          <FileText className="w-8 h-8" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2">{t('no_report_generated_title')}</h3>
        <p className="text-slate-500 max-w-md mb-8">
          {t('no_report_generated_desc')}
        </p>
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="bg-blue-600 hover:bg-blue-700 h-11 px-8"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('generating_report')}
            </>
          ) : (
            t('generate_report_btn')
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center bg-blue-50 p-4 rounded-md border border-blue-100">
        <div className="flex items-center gap-2">
          <FileBadge className="w-5 h-5 text-blue-600" />
          <p className="text-sm text-blue-700 font-medium">{t('generated_on')} {new Date(report.generated_at).toLocaleDateString()} {t('at_time')} {new Date(report.generated_at).toLocaleTimeString()}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="bg-white hover:bg-slate-50 text-slate-700 border-slate-300 shadow-sm">
                {t('edit_report_btn')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(api.exportPdf(caseId), "_blank")} className="bg-white hover:bg-slate-50 text-slate-700 border-slate-300 shadow-sm">
                <Download className="w-3 h-3 mr-2" />
                {t('download_pdf_btn')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleRevert} disabled={reverting} className="bg-white hover:bg-slate-50 text-slate-600 border-blue-200 shadow-sm" title={t('revert_to_original')}>
                {reverting ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <RefreshCcw className="w-3 h-3 mr-2" />}
                {t('revert_to_original')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>
                {t('cancel')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? t('saving') : t('save_changes_btn')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-amber-200 shadow-sm bg-amber-50/20 md:col-span-2">
          <CardHeader className="bg-amber-50/50 border-b border-amber-100 pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-amber-800 flex items-center gap-2 text-lg font-['Manrope']">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> {t('apparent_contradictions_title')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editContradictions : (report.apparent_contradictions || []), t('apparent_contradictions_title'))} title="Copy section">
              <span className="sr-only">Copy</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-amber-400 hover:text-amber-600"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <CardDescription className="mb-4 text-amber-700/80">
              {t('apparent_contradictions_desc')}
            </CardDescription>

            <div className="space-y-3">
              {(isEditing ? editContradictions : (report.apparent_contradictions || [])).map((item, i) => (
                <div key={i} className="flex gap-3 items-start p-3 bg-white/60 rounded-md border border-amber-100">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  {isEditing ? (
                    <div className="flex-1 flex gap-2 w-full">
                      <Textarea
                        value={item}
                        onChange={(e) => handleContradictionChange(i, e.target.value)}
                        className="min-h-[60px] bg-white border-amber-200 focus-visible:ring-amber-500 w-full"
                        placeholder={t('describe_inconsistency')}
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeContradiction(i)} className="text-amber-400 hover:text-red-500 hover:bg-amber-100 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-slate-800 text-sm leading-relaxed">{item}</p>
                  )}
                </div>
              ))}
            </div>

            {(!report.apparent_contradictions || report.apparent_contradictions.length === 0) && !isEditing && (
              <div className="flex items-center gap-2 text-slate-500 italic text-sm p-4 bg-white/50 rounded-md border border-dashed border-slate-200 justify-center">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t('no_contradictions')}
              </div>
            )}

            {isEditing && (
              <Button variant="outline" size="sm" onClick={addContradiction} className="mt-4 w-full border-dashed border-amber-300 text-amber-700 hover:bg-amber-50">
                + {t('add_contradiction')}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm md:col-span-2">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-['Manrope']">{t('report_summary')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editSummary : report.executive_summary, t('report_summary'))} title="Copy section">
              <span className="sr-only">Copy</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-slate-400 hover:text-slate-600"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            {isEditing ? (
              <Textarea
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                className="min-h-[100px]"
              />
            ) : (
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                {report.executive_summary || <span className="text-slate-400 italic">{t('no_summary')}</span>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SOAP Sections */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg font-['Manrope']">{t('subjective_title')}</CardTitle>
                <CardDescription>{t('subjective_subtitle')}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editSubjective : report.subjective, t('subjective_title'))} title="Copy section">
                <span className="sr-only">Copy</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-slate-400 hover:text-slate-600"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {isEditing ? (
              <Textarea
                value={editSubjective}
                onChange={(e) => setEditSubjective(e.target.value)}
                className="min-h-[150px]"
                placeholder={t('subjective_placeholder')}
              />
            ) : (
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                {report.subjective || <span className="text-slate-400 italic">{t('no_subjective')}</span>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg font-['Manrope']">{t('objective_title')}</CardTitle>
                <CardDescription>{t('objective_subtitle')}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editObjective : report.objective, t('objective_title'))} title="Copy section">
                <span className="sr-only">Copy</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-slate-400 hover:text-slate-600"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {isEditing ? (
              <Textarea
                value={editObjective}
                onChange={(e) => setEditObjective(e.target.value)}
                className="min-h-[150px]"
                placeholder={t('objective_placeholder')}
              />
            ) : (
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                {report.objective || <span className="text-slate-400 italic">{t('no_objective')}</span>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg font-['Manrope']">{t('assessment_title')}</CardTitle>
                <CardDescription>{t('assessment_subtitle')}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editAssessment : report.assessment, t('assessment_title'))} title="Copy section">
                <span className="sr-only">Copy</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-slate-400 hover:text-slate-600"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {isEditing ? (
              <Textarea
                value={editAssessment}
                onChange={(e) => setEditAssessment(e.target.value)}
                className="min-h-[150px]"
                placeholder={t('assessment_placeholder')}
              />
            ) : (
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                {report.assessment || <span className="text-slate-400 italic">{t('no_assessment')}</span>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg font-['Manrope']">{t('plan_title')}</CardTitle>
                <CardDescription>{t('plan_subtitle')}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editPlan : report.plan, t('plan_title'))} title="Copy section">
                <span className="sr-only">Copy</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-slate-400 hover:text-slate-600"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {isEditing ? (
              <Textarea
                value={editPlan}
                onChange={(e) => setEditPlan(e.target.value)}
                className="min-h-[150px]"
                placeholder={t('plan_placeholder')}
              />
            ) : (
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                {report.plan || <span className="text-slate-400 italic">{t('no_plan')}</span>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Card className="border-emerald-100 shadow-sm">
          <CardHeader className="bg-emerald-50/30 border-b border-emerald-100 pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-emerald-800 flex items-center gap-2 text-lg font-['Manrope']">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> {t('key_strengths_title')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editStrengths : report.key_strengths, t('key_strengths_title'))} title="Copy section">
              <span className="sr-only">Copy</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-emerald-600 hover:text-emerald-800"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3">
              {(isEditing ? editStrengths : (report.key_strengths || [])).map((str, i) => (
                <li key={i} className="flex gap-3 text-slate-700">
                  <span className="text-emerald-500 mt-1.5 flex-shrink-0">•</span>
                  {isEditing ? (
                    <div className="flex-1 flex gap-2">
                      <input
                        className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                        value={str}
                        onChange={(e) => handleStrengthChange(i, e.target.value)}
                      />
                      <button onClick={() => removeStrength(i)} className="text-slate-400 hover:text-red-500">×</button>
                    </div>
                  ) : (
                    <span>{str}</span>
                  )}
                </li>
              ))}
            </ul>
            {(!report.key_strengths || report.key_strengths.length === 0) && !isEditing && (
              <p className="text-sm text-slate-400 italic">{t('no_strengths')}</p>
            )}
            {isEditing && (
              <Button variant="outline" size="sm" onClick={addStrength} className="mt-4 w-full border-dashed border-slate-300">
                + {t('add_strength_btn')}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="border-blue-100 shadow-sm">
          <CardHeader className="bg-blue-50/30 border-b border-blue-100 pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-blue-800 flex items-center gap-2 text-lg font-['Manrope']">
              <Target className="w-5 h-5 text-blue-600" /> {t('action_plan_title')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => copySection(isEditing ? editActionPlan : report.action_plan, t('action_plan_title'))} title="Copy section">
              <span className="sr-only">Copy</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy text-blue-600 hover:text-blue-800"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {(isEditing ? editActionPlan : (report.action_plan || [])).map((item, i) => (
                <div key={i} className="bg-slate-50 p-4 rounded-md border border-slate-100 hover:border-blue-200 transition-colors">
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <label className="text-xs font-bold text-slate-500 uppercase">{t('domain_label')}</label>
                        <button onClick={() => removeActionGoal(i)} className="text-slate-400 hover:text-red-500">{t('remove_btn')}</button>
                      </div>
                      <input
                        className="w-full border border-slate-300 rounded px-2 py-1 text-sm font-bold text-slate-500 uppercase tracking-wider"
                        value={item.domain}
                        onChange={(e) => handleActionPlanChange(i, "domain", e.target.value)}
                        placeholder={t('domain_placeholder')}
                      />
                      <label className="text-xs font-bold text-slate-500 uppercase block mt-2">{t('workflow_goal') || 'Goal'}</label>
                      <textarea
                        className="w-full border border-slate-300 rounded px-2 py-1 text-sm font-medium"
                        value={item.goal}
                        onChange={(e) => handleActionPlanChange(i, "goal", e.target.value)}
                        placeholder={t('goal_placeholder')}
                        rows={2}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">
                        {t(`domain_${item.domain?.toLowerCase().replace(/ /g, '_').replace(/'/g, '')}`) !== `domain_${item.domain?.toLowerCase().replace(/ /g, '_').replace(/'/g, '')}`
                          ? t(`domain_${item.domain?.toLowerCase().replace(/ /g, '_').replace(/'/g, '')}`)
                          : item.domain}
                      </p>
                      <p className="text-sm text-slate-800 font-medium">{item.goal}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
            {(!report.action_plan || report.action_plan.length === 0) && !isEditing && (
              <p className="text-sm text-slate-400 italic">{t('no_action_plan')}</p>
            )}
            {isEditing && (
              <Button variant="outline" size="sm" onClick={addActionGoal} className="mt-4 w-full border-dashed border-slate-300">
                + {t('add_action_goal_btn')}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>


    </div>
  );
}

export default function Results() {
  const { caseId } = useParams();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [caseData, setCaseData] = useState(null);

  // Sync UI language with case target language
  useEffect(() => {
    if (caseData?.target_language && currentLanguage && caseData.target_language !== currentLanguage.code) {
      // Find the language object in allLanguages (flattening dialects if necessary)
      let targetLang = allLanguages.find(l => l.code === caseData.target_language);

      if (!targetLang) {
        // Check dialects
        for (const l of allLanguages) {
          if (l.dialects) {
            const d = l.dialects.find(dial => dial.code === caseData.target_language);
            if (d) {
              targetLang = d;
              break;
            }
          }
        }
      }

      if (targetLang) {
        changeLanguage(targetLang);
      }
    }
  }, [caseData?.target_language, currentLanguage?.code, changeLanguage]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("results"); // results, report

  const currentUser = auth.getUser();
  const isAdmin = auth.isAdmin();

  const [report, setReport] = useState(null);

  useEffect(() => {
    loadData();
  }, [caseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [caseRes, scoresRes, reportRes] = await Promise.all([
        api.getCase(caseId),
        api.getScores(caseId),
        api.getReport(caseId).catch(() => ({ data: null }))
      ]);
      setCaseData(caseRes.data);
      setScores(scoresRes.data);
      setReport(reportRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateScore = async (domainKey, data) => {
    try {
      await api.updateScore(caseId, domainKey, data);
      const [caseRes, scoresRes] = await Promise.all([
        api.getCase(caseId),
        api.getScores(caseId)
      ]);
      setCaseData(caseRes.data);
      setScores(scoresRes.data);
    } catch (error) {
      throw error;
    }
  };

  const calculateTotal = () => {
    return scores.reduce((sum, s) => {
      const val = s.human_score !== null ? s.human_score : (s.ai_score || 0);
      return sum + val;
    }, 0);
  };

  const getUnresolvedCount = () => {
    return scores.filter(s => {
      const effective = s.human_score !== null ? s.human_score : (s.ai_score || 0);
      if (s.needs_human_review && !s.human_override && effective === 0) return true;
      if (["low", "medium"].includes(s.ai_confidence) && !s.human_override) return true;
      return false;
    }).length;
  };

  const handleSubmitClick = () => {
    setShowSubmitDialog(true);
  };

  const handleSubmitConfirm = async () => {
    setSubmitting(true);
    try {
      await api.submitCase(caseId);
      toast.success("Assessment Submitted Successfully! The assessment is now locked.");
      setShowSubmitDialog(false);
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Submission failed";
      toast.error(errorMsg);
      console.error("Submission error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportPdf = () => {
    window.open(api.exportPdf(caseId), "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  const sortedScores = [...scores].sort((a, b) => {
    const aUnresolved = (a.needs_human_review && !a.human_override) || (["low", "medium"].includes(a.ai_confidence) && !a.human_override);
    const bUnresolved = (b.needs_human_review && !b.human_override) || (["low", "medium"].includes(b.ai_confidence) && !b.human_override);

    if (aUnresolved && !bUnresolved) return -1;
    if (!aUnresolved && bUnresolved) return 1;
    return 0;
  });

  const isSubmitted = caseData?.status === "submitted";
  const isLocked = isSubmitted && !isAdmin;
  const unresolvedCount = getUnresolvedCount();
  const totalScore = calculateTotal();
  const goalsCount = scores.filter((s) => s.participant_goal).length;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/case/${caseId}`} data-testid="back-to-case">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="results-title">
                  Matrix Results
                  {isSubmitted && <Badge className="ml-2 bg-emerald-600">Submitted</Badge>}
                </h1>
                <p className="text-sm text-slate-500">{caseData?.client_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button
                onClick={handleExportPdf}
                className="bg-slate-700 hover:bg-slate-800 rounded-sm"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-md mb-6 w-fit">
          <button
            onClick={() => setActiveTab("results")}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-all ${activeTab === "results"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
              }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              {t('assessment_scores_tab')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab("report")}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-all ${activeTab === "report"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
              }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('case_report_tab')}
              <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] h-5 px-1.5 ml-1">NEW</Badge>
            </div>
          </button>
        </div>

        {activeTab === "results" ? (
          <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
            {/* Detected Inconsistencies Warning (From Report) */}
            {report && report.apparent_contradictions && report.apparent_contradictions.length > 0 && (
              <Card className="border-amber-200 shadow-sm bg-amber-50/20 mb-6">
                <CardHeader className="bg-amber-50/50 border-b border-amber-100 pb-4">
                  <CardTitle className="text-amber-800 flex items-center gap-2 text-lg font-['Manrope']">
                    <AlertTriangle className="w-5 h-5 text-amber-600" /> {t('apparent_contradictions_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {report.apparent_contradictions.map((item, i) => (
                      <div key={i} className="flex gap-3 items-start p-3 bg-white/60 rounded-md border border-amber-100">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-800 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {unresolvedCount > 0 && !isSubmitted && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-sm mb-6 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-medium">
                  {unresolvedCount} {t('require_review_msg')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-slate-900 tabular-nums">
                    {totalScore}
                  </div>
                  <div className="text-sm text-slate-500">{t('total_score_label')}</div>
                </CardContent>
              </Card>
              <Card className={`border-slate-200 rounded-sm ${unresolvedCount > 0 && !isSubmitted ? "bg-amber-50" : ""}`}>
                <CardContent className="p-4 text-center">
                  <div className={`text-3xl font-bold tabular-nums ${unresolvedCount > 0 && !isSubmitted ? "text-amber-700 animate-pulse" : "text-slate-900"}`}>
                    {unresolvedCount}
                  </div>
                  <div className={`text-sm ${unresolvedCount > 0 && !isSubmitted ? "text-amber-600" : "text-slate-500"}`}>
                    {t('pending_review_label')}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-700 tabular-nums">
                    {goalsCount}
                  </div>
                  <div className="text-sm text-slate-500">{t('goals_set_label')}</div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-slate-200 rounded-sm overflow-hidden">
              <CardHeader className="border-b border-slate-200 bg-slate-50">
                <CardTitle className="text-lg font-['Manrope']">{t('domain_scores_title')}</CardTitle>
                <CardDescription>
                  {isLocked ? t('assessment_locked_msg') : t('review_ai_scores_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex items-center gap-4 p-4 bg-slate-100 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <div className="flex-1">{t('domain_label')}</div>
                  <div className="text-center w-20">{t('ai_score_label')}</div>
                  <div className="text-center w-24">{t('confidence_label')}</div>
                  <div className="text-center w-20">{t('final_score_label')}</div>
                  <div className="w-8"></div>
                </div>

                {sortedScores.map((score) => (
                  <DomainRow
                    key={score.domain_key}
                    score={score}
                    onUpdate={handleUpdateScore}
                    isLocked={isLocked}
                  />
                ))}
              </CardContent>
            </Card>
          </div>
        ) : (
          <ReportView caseId={caseId} />
        )}

        <ActivityLog caseData={caseData} />
      </main>

      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('submit_assessment_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('submit_assessment_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitConfirm}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('submitting')}
                </>
              ) : (
                t('submit_confirm_btn')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
